import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getCredits, getPricing, createPortalSession } from "@/lib/api";
import Header from "@/components/Header";
import { ArrowRight, User as UserIcon, Zap, Calendar, CreditCard, Settings } from "lucide-react";

// /account — Grammarly/Netflix-style account page. Billing management lives HERE,
// behind the profile avatar, never on the pages where customers do their daily work.
export default function Account() {
  const { user, loading } = useAuth();
  const [credits, setCredits] = useState(null);
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { setBusy(false); return; }
    (async () => {
      try {
        const [c, pk] = await Promise.all([
          getCredits().catch(() => null),
          getPricing().catch(() => []),
        ]);
        setCredits(c);
        setPackages(pk || []);
      } finally {
        setBusy(false);
      }
    })();
  }, [user, loading]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (e) {
      setPortalLoading(false);
    }
  };

  if (loading || busy) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 font-mono-plex text-sm" data-testid="account-loading">
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 text-center" data-testid="account-signed-out">
          <h1 className="font-serif-display text-4xl font-black">Your account</h1>
          <p className="mt-3 text-sm text-[#333]">Sign in to see your plan and billing details.</p>
          <button
            onClick={handleLogin}
            className="mt-6 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
            data-testid="account-signin-button"
          >
            Sign in <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  const hasSub = credits?.subscription_status === "active";
  const currentPkg = packages.find((p) => p.id === credits?.subscription_plan);
  const planName = hasSub ? (currentPkg?.name || "Monthly plan") : null;

  let renewsOn = null;
  if (hasSub && credits?.subscription_expires_at) {
    renewsOn = new Date(credits.subscription_expires_at).toLocaleDateString(undefined, {
      month: "long", day: "numeric", year: "numeric",
    });
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12" data-testid="account-page">
        <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
          § Account
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl font-black tracking-tight leading-none">
          Your account
        </h1>

        {/* Profile */}
        <section className="mt-8 border-2 border-[#111] bg-white p-6 flex items-center gap-4" data-testid="account-profile">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-14 h-14 border-2 border-[#111] object-cover" />
          ) : (
            <div className="w-14 h-14 border-2 border-[#111] bg-[#F4F4F0] flex items-center justify-center">
              <UserIcon size={24} />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-serif-display text-2xl font-bold leading-tight truncate">{user.name}</div>
            <div className="font-mono-plex text-xs text-[#555] truncate">{user.email}</div>
          </div>
        </section>

        {/* Plan + billing */}
        <section className="mt-6 border-2 border-[#111] bg-white" data-testid="account-plan">
          <div className="p-6 border-b-2 border-[#111]">
            <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1.5">
              <CreditCard size={11} /> ▸ Plan
            </div>
            {hasSub ? (
              <>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <span className="font-serif-display text-3xl font-black leading-none">{planName}</span>
                  {currentPkg?.price != null && (
                    <span className="font-mono-plex text-xs text-[#555]">${currentPkg.price}/mo</span>
                  )}
                  <span className="font-mono-plex text-[10px] uppercase tracking-widest bg-[#FF331F] text-white px-2 py-1">
                    Active
                  </span>
                </div>
                {renewsOn && (
                  <div className="mt-3 font-mono-plex text-xs text-[#555] flex items-center gap-1.5" data-testid="account-renewal">
                    <Calendar size={12} />
                    {credits.auto_renew ? `Renews automatically on ${renewsOn}` : `Access until ${renewsOn}`}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="mt-2 font-serif-display text-3xl font-black leading-none">
                  {(credits?.credits ?? 0) > 0 ? "Pay as you go" : "No active plan"}
                </div>
                <p className="mt-2 text-sm text-[#333]">
                  {(credits?.credits ?? 0) > 0
                    ? "You're using one-time lookups. Upgrade to a monthly plan for more volume."
                    : "Pick a plan to start analyzing."}
                </p>
              </>
            )}
          </div>
          <div className="p-6 border-b-2 border-[#111]">
            <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1.5">
              <Zap size={11} /> ▸ Lookups
            </div>
            <div className="mt-2 font-serif-display text-3xl font-black leading-none" data-testid="account-lookups">
              {credits?.total ?? 0}
            </div>
            <div className="mt-1 font-mono-plex text-xs text-[#555]">
              {hasSub
                ? `${credits.monthly_credits} monthly · ${credits.credits} bonus pack`
                : "Never expire"}
            </div>
          </div>
          <div className="p-6 flex flex-wrap items-center gap-3">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 border-2 border-[#111] px-4 py-2.5 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0]"
              data-testid="account-change-plan"
            >
              {hasSub ? "Change plan" : "See plans"} <ArrowRight size={12} />
            </Link>
            {hasSub && credits.auto_renew && (
              <button
                onClick={openPortal}
                disabled={portalLoading}
                className="inline-flex items-center gap-2 border-2 border-[#111] px-4 py-2.5 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0] disabled:opacity-50"
                data-testid="manage-subscription-btn"
              >
                <Settings size={12} /> {portalLoading ? "Opening..." : "Manage subscription"}
              </button>
            )}
          </div>
        </section>

        <p className="mt-4 font-mono-plex text-[10px] uppercase tracking-widest text-[#888]">
          * Payments, invoices and cancellation are handled securely by Stripe.
        </p>
      </main>
    </div>
  );
}
